/**
 * Returns a function that runs callbacks at most once per `ms` for each key. The first call for a
 * key runs immediately; calls inside the interval after a run are held until the interval ends,
 * and only the latest of them runs then.
 *
 * @template K
 * @param {number} ms
 * @returns {(key: K, callback: () => void) => void}
 */
export function keyedThrottle(ms) {
	/** @type {Map<K, (() => void) | undefined>} */
	const cooling = new Map();

	/**
	 * @param {K} key
	 * @param {() => void} callback
	 */
	function run(key, callback) {
		cooling.set(key, undefined);
		setTimeout(() => {
			const held = cooling.get(key);
			cooling.delete(key);
			if (held) run(key, held);
		}, ms);
		callback();
	}

	return (key, callback) => {
		if (cooling.has(key)) cooling.set(key, callback);
		else run(key, callback);
	};
}
